import React, { useState, useEffect } from 'react';
import axios from '../../axios';
import config from '../../config';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import './BrokerProfile.css';
import NewConversation from '../Conversation/NewConversation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faUserTie } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../i18n/LanguageContext';

function BrokerProfile() {
  const { brokerId } = useParams();
  const history = useHistory();
  const location = useLocation();
  // Read current user data from localStorage
  const currentUserId = localStorage.getItem('userId'); // stored as a string
  const currentUserType = localStorage.getItem('userType');

  const [broker, setBroker] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ rating: 1, comment: '' });
  const [userReviewExists, setUserReviewExists] = useState(false);
  const [error, setError] = useState('');
  const [showConversationForm, setShowConversationForm] = useState(false);
  const backend_url = config.backend_url;
  const { t } = useLanguage();

  useEffect(() => {
    fetchBroker();
    fetchReviews();
  }, [brokerId]);

  const fetchBroker = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/brokers/${brokerId}`);
      setBroker(response.data);
    } catch (err) {
      console.error("Error fetching broker info:", err);
      setError(t('brokerProfile.loadError'));
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/brokers/${brokerId}/reviews`);
      setReviews(response.data);

      // Only check for an existing review if the current user is a seller
      if (currentUserType === 'seller' && currentUserId) {
        const userReview = response.data.find(
          review => review.reviewer_id === parseInt(currentUserId, 10)
        );
        if (userReview) {
          setNewReview({ rating: userReview.rating, comment: userReview.comment });
          setUserReviewExists(true);
        } else {
          setUserReviewExists(false);
          setNewReview({ rating: 1, comment: '' });
        }
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError(t('brokerProfile.reviewLoadError'));
    }
  };

  // Calculate the average rating
  const averageRating = reviews.length
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      if (userReviewExists) {
        // Update the existing review
        await axios.put(`${backend_url}/api/brokers/${brokerId}/review`, newReview, { withCredentials: true });
      } else {
        // Create a new review
        await axios.post(`${backend_url}/api/brokers/${brokerId}/review`, newReview, { withCredentials: true });
      }
      fetchReviews();
    } catch (err) {
      console.error("Error submitting review:", err);
      setError(t('brokerProfile.reviewSubmitError'));
    }
  };

  const handleDeleteReview = async () => {
    try {
      await axios.delete(`${backend_url}/api/brokers/${brokerId}/review`, { withCredentials: true });
      setNewReview({ rating: 1, comment: '' });
      setUserReviewExists(false);
      fetchReviews();
    } catch (err) {
      console.error("Error deleting review:", err);
      setError(t('brokerProfile.reviewDeleteError'));
    }
  };

  const handleBackClick = () => {
    // Check if we have state with a conversationId passed from the referring page
    if (location.state && location.state.fromConversation) {
      // Navigate back to the specific conversation
      history.push(`/conversation/${location.state.fromConversation}`);
    } else {
      // Default back behavior
      history.goBack();
    }
  };

  return (
    <div className="broker-profile-container">
      <h1><FontAwesomeIcon icon={faUserTie} className="broker-icon" /> {t('brokerProfile.title')}</h1>
      
      {/* Back Button */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={handleBackClick}
          style={{ 
            padding: '8px 16px',
            backgroundColor: 'var(--blue-3)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'background-color 0.2s'
          }}
        >
          {t('common.back')}
        </button>
      </div>
      
      {/* Broker Information */}
      {broker ? (
        <div className="broker-info-section">
          {/* Profile Image */}
          <div className="broker-profile-image-container">
            {broker.profile_image_url ? (
              <img
                src={broker.profile_image_url}
                alt={`${broker.firstname} ${broker.lastname}`}
                className="broker-profile-image"
              />
            ) : (
              <div className="broker-no-profile-image">
                <span>{t('brokerProfile.noProfileImage')}</span>
              </div>
            )}
          </div>
          
          <div className="broker-details">
            <h2>{broker.firstname} {broker.lastname}</h2>
            {broker.company_name && broker.company_name !== "NULL" && (
              <div className="broker-company-info">
                <p><strong>{t('brokerProfile.company')}:</strong> {broker.company_name}
                {broker.company_chain && broker.company_chain !== "NULL" && ` (${broker.company_chain})`}</p>
              </div>
            )}
            <p className="broker-description">{broker.description || t('brokerProfile.noDescription')}</p>
            
            {/* Conversation Button - Only show for sellers */}
            {currentUserType === 'seller' && !showConversationForm && (
              <div style={{ marginTop: '1.5rem' }}>
                <button
                  onClick={() => setShowConversationForm(true)}
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'var(--blue-3)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <FontAwesomeIcon icon={faComments} style={{ marginRight: '8px' }} />
                  {t('brokerProfile.startConversation')}
                </button>
              </div>
            )}
            
            {/* Conversation Form */}
            {currentUserType === 'seller' && showConversationForm && (
              <div style={{
                marginTop: '1.5rem',
                backgroundColor: 'var(--white)',
                borderRadius: '10px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                border: '1px solid var(--duck-blue-7)'
              }}>
                <h3 style={{ 
                  marginTop: '0', 
                  marginBottom: '1rem', 
                  color: 'var(--blue-1)',
                  borderBottom: '1px solid var(--duck-blue-7)',
                  paddingBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <FontAwesomeIcon icon={faComments} style={{ marginRight: '10px', color: 'var(--blue-3)' }} />
                  {t('brokerProfile.newConversation')}
                </h3>
                <NewConversation
                  preselectedBrokerId={brokerId}
                  preselectedObjectLink={null}
                  streetName={broker.location || ""}
                  onConversationCreated={(conversationId) => {
                    setShowConversationForm(false);
                    // If we have a conversation ID, navigate directly to it
                    if (conversationId) {
                      history.push(`/conversation/${conversationId}`);
                    } else {
                      history.push('/inbox');
                    }
                  }}
                  onCancel={() => setShowConversationForm(false)}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <p>{t('brokerProfile.loadingBroker')}</p>
      )}

      {/* Reviews Section */}
      <div className="reviews-section">
        <h3>{t('brokerProfile.reviews')}</h3>
        {reviews.length > 0 ? (
          <>
            {/* Display average rating */}
            <p className="average-rating">{t('brokerProfile.averageRating')}: <span>{averageRating}</span></p>
            <table className="reviews-table">
              <thead>
                <tr>
                  <th>{t('brokerProfile.rating')}</th>
                  <th>{t('brokerProfile.comment')}</th>
                  <th>{t('brokerProfile.date')}</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review.id}>
                    <td>{review.rating}</td>
                    <td>{review.comment}</td>
                    <td>{review.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p>{t('brokerProfile.noReviews')}</p>
        )}
      </div>

      {/* Add Review Form */}
      {currentUserType === 'seller' && (
        <div className="add-review-section">
          <h3>{userReviewExists ? t('brokerProfile.editReview') : t('brokerProfile.addReview')}</h3>
          <form onSubmit={handleReviewSubmit} className="review-form">
            <div className="form-group">
              <label htmlFor="rating">
                {t('brokerProfile.rating')}:&nbsp;
                <select
                  id="rating"
                  value={newReview.rating}
                  onChange={(e) =>
                    setNewReview({ ...newReview, rating: parseInt(e.target.value, 10) })
                  }
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                  <option value={5}>5</option>
                </select>
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="comment">
                {t('brokerProfile.comment')}:&nbsp;
                <textarea
                  id="comment"
                  value={newReview.comment}
                  onChange={(e) =>
                    setNewReview({ ...newReview, comment: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="submit-button">
                {userReviewExists ? t('brokerProfile.updateReview') : t('brokerProfile.submitReview')}
              </button>
              {userReviewExists && (
                <button 
                  type="button" 
                  onClick={handleDeleteReview}
                  className="delete-button"
                >
                  {t('brokerProfile.deleteReview')}
                </button>
              )}
            </div>
          </form>
        </div>
      )}
      
      {/* Error Message */}
      {error && <p className="error-message">{error}</p>}
    </div>
  );
}

export default BrokerProfile;
